import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from 'lucide-react';
import { LeftPanel } from '../components/game/LeftPanel';
import { CenterPanel } from '../components/game/CenterPanel';
import { RightPanel } from '../components/game/RightPanel';
import { ActionBar } from '../components/game/ActionBar';
import { AccusationModal } from '../components/modals/AccusationModal';
import { GameHelp } from '../components/game/GameHelp';
import { MobilePanelTabs } from '../components/game/MobilePanelTabs';
import { TabletPanelToggle } from '../components/game/TabletPanelToggle';
import { GameStatusBar } from '../components/game/GameStatusBar';
import { GameSessionGate } from '../components/game/GameSessionGate';
import { useGameSession } from '../hooks/useGameSession';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useGameStore } from '../store/gameStore';
import { caseHasScenes } from '../components/game3d/sceneRegistry';
import { VIEW_MODE_3D, hasWebGL2, writeViewPreference } from '../utils/gameView';

export const GamePage = () => {
  const navigate = useNavigate();
  // Session lifecycle (generation, recovery, featured compat load, restore)
  // lives in the shared hook so /game3d can run on the same logic.
  const session = useGameSession();
  const { sessionId: storedSessionId, theme, isFeaturedSession } = session;
  const locations = useGameStore((state) => state.locations);
  const [showAccusationModal, setShowAccusationModal] = React.useState(false);

  // The 3D view exists only for featured cases with captured rooms, on a
  // device that can render them: a fine pointer (tracked live), WebGL2
  // (probed once, and only once the rest already holds) and a desktop-width
  // window, which the button's `lg:` classes enforce.
  const sceneCase = useMemo(
    () => isFeaturedSession && caseHasScenes(locations),
    [isFeaturedSession, locations],
  );
  const finePointer = useMediaQuery('(pointer: fine)');
  const webgl2 = useMemo(() => sceneCase && finePointer && hasWebGL2(), [sceneCase, finePointer]);
  const show3DSwitch = sceneCase && finePointer && webgl2;
  const switchTo3D = () => {
    writeViewPreference(VIEW_MODE_3D);
    navigate('/game3d', { replace: true });
  };

  // Responsive panel state
  const [mobileActivePanel, setMobileActivePanel] = useState('scene'); // Mobile: which panel is shown
  const [tabletActivePanel, setTabletActivePanel] = useState('scene'); // Tablet: scene or clues in right section

  return (
    <GameSessionGate session={session}>
      <div className={`min-h-screen bg-gradient-to-br ${theme.bg} flex flex-col pb-24`}>
        {/* Status Bar - Top, all breakpoints. The 3D switch rides its right
            end (desktop only), clear of the centred chips; the fixed evidence
            panel starts below the bar. */}
        <div className="relative">
          <GameStatusBar />
          {show3DSwitch && (
            <button
              type="button"
              onClick={switchTo3D}
              aria-label="Switch to 3D view"
              title="Switch to 3D view"
              className="hidden lg:flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-1.5 px-2 py-1 bg-black/30 border border-white/10 rounded-lg text-xs md:text-sm text-white font-medium hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <Box className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: theme.accent }} aria-hidden="true" />
              3D view
            </button>
          )}
        </div>

        {/* Desktop Layout: 3 columns (>= 1024px) */}
        <div className="hidden lg:block flex-1 overflow-y-auto pr-[20%]">
          {/* Left and Center panels - scrollable */}
          <div className="flex">
            <div className="w-[37.5%] border-r border-white/10">
              <LeftPanel />
            </div>
            <div className="w-[62.5%] border-r border-white/10">
              <CenterPanel />
            </div>
          </div>
        </div>
        {/* Right panel - fixed position on desktop */}
        <div className="hidden lg:block fixed top-[3.5rem] right-0 w-[20%] h-[calc(100vh-3.5rem-6rem)] overflow-y-auto">
          <RightPanel />
        </div>

        {/* Tablet Layout: 2 columns with toggle (768px - 1023px) */}
        <div className="hidden md:flex lg:hidden flex-1 overflow-hidden">
          {/* Left: Always show Character panel */}
          <div className="w-[45%] border-r border-white/10 overflow-y-auto">
            <LeftPanel />
          </div>
          {/* Right: Toggle between Scene and Clues */}
          <div className="w-[55%] flex flex-col overflow-hidden">
            <TabletPanelToggle
              activePanel={tabletActivePanel}
              setActivePanel={setTabletActivePanel}
            />
            <div className="flex-1 overflow-y-auto">
              {tabletActivePanel === 'scene' ? <CenterPanel /> : <RightPanel />}
            </div>
          </div>
        </div>

        {/* Mobile Layout: Tab-based navigation (< 768px) */}
        <div className="md:hidden flex-1 flex flex-col overflow-hidden">
          <MobilePanelTabs
            activePanel={mobileActivePanel}
            setActivePanel={setMobileActivePanel}
          />
          <div className="flex-1 overflow-y-auto pb-4">
            {mobileActivePanel === 'character' && <LeftPanel />}
            {mobileActivePanel === 'scene' && <CenterPanel />}
            {mobileActivePanel === 'clues' && <RightPanel />}
          </div>
        </div>

        {/* Action Bar - Sticky Bottom */}
        {/* The store is the single source of truth for the session id: a local
            copy went stale on every restore (it was only rewritten after a
            successful generation). */}
        <ActionBar sessionId={storedSessionId} onAccuse={() => setShowAccusationModal(true)} />

        {/* Help/Tutorial */}
        <GameHelp />

        {/* Accusation Modal */}
        {showAccusationModal && storedSessionId && (
          <AccusationModal
            sessionId={storedSessionId}
            isOpen={showAccusationModal}
            onClose={() => setShowAccusationModal(false)}
            onCaseClose={() => navigate('/')}
          />
        )}
      </div>
    </GameSessionGate>
  );
};
